import React, { ReactNode, useState, } from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import useListingPagination from "../../common/hooks/UseListingPagination"
import Icon from "../../common/icon/icon.component.jsx"
import { BasicRow } from "../../common/listing/ListingTypes"
import ListingWrapper from "../../common/listing/ListingWrapper"
import SortLink from "../../common/listing/sortlink.component.jsx"
import Search from "../../common/search-filter/search/search.component"
import { GlobalHelper } from "../../indexImports"
import { Intl } from "../../state/helpers/IntlProps"
import { SerialNumberElastic } from "../../state/listing/serial-numbers/models"
import { SerialNumberListingService } from "../../state/listing/serial-numbers/service"
import { STOCK_DOCUMENTS } from "../../utils/inventory"
import { PRICING_TABLES } from "../../utils/pricing/constants"
import EditSerialNumberModal from "./modal/EditSerialNumberModal"

const messages = defineMessages({
    search_placeholder: {
        id: "product.serial_number.listing.search_placeholder.label",
        defaultMessage: "Search for serial numbers...",
    }
})

type Props = {
    productId: string
    intl: Intl
}

const OBJECT_TYPE = "serial-numbers"

const SerialNumberListing = (props: Props) => {
    const { productId } = props


    const {
        paginatedSearch,
        handleSorting,
        handleKeywordsChange,
        updateTotalItemsCount,
        pagination
    } = useListingPagination({})

    const {
        error,
        isLoading,
        data: serialNumberData,
        refetch: refetchListing
    } = useQuery(["serial-numbers", productId, paginatedSearch], () => {
        return SerialNumberListingService.findMultipleByProductId({
            ...paginatedSearch,
            productId: productId,
            keywords: GlobalHelper.formatSearchKeywords(paginatedSearch.keywords)
        })
    }, {
        cacheTime: 0
    })

    const getRow = (serialNumber: SerialNumberElastic): BasicRow => {
        let saleLink = <></>
        const mostRecentSale = serialNumber.getMostRecentActiveSale()
        if (mostRecentSale) {
            const docType = mostRecentSale.saleType == PRICING_TABLES 
                ? STOCK_DOCUMENTS
                : mostRecentSale.saleType
            saleLink = <Link to={"/" + docType + "/" + mostRecentSale.saleId + "/edit"}>{mostRecentSale.identifier}</Link>
        }

        return {
            rowId: serialNumber.getId(),
            data: [
                <div key={0}>
                    {serialNumber.getSerial()}
                </div>,
                <div key={0}>
                    {saleLink}
                </div>,
                <button
                    key={1}
                    className="btn btn-primary"
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        setModal(
                            <EditSerialNumberModal
                                serialNumberId={serialNumber.getId()}
                                serialNumber={serialNumber}
                                onCloseCallback={() => {
                                    setModal(null)
                                }}
                                onSuccessCallback={() => {
                                    setModal(null)
                                    refetchListing()
                                }}
                            />
                        )
                    }}>
                    <FormattedMessage
                        id="product.serial_number.listing.edit.label"
                        defaultMessage="Edit"
                    />
                </button>
            ],
            classNames: ["", "", "d-flex justify-content-end"]
        }
    }

    const [modal, setModal] = useState<ReactNode>(null)
    const totalItemsCount = serialNumberData?.getTotalItemsCount() || 0
    updateTotalItemsCount(totalItemsCount)

    return <>
        {modal}

        <ListingWrapper
            isLoading={isLoading}
            error={error as Error}
            standardListProps={{
                className: "row pt-3",
                headers: {
                    data: [
                        <SortLink
                            key={0}
                            field={paginatedSearch.sortByField}
                            sortField={paginatedSearch.sortByField}
                            asc={paginatedSearch.sortAsc}
                            onChange={handleSorting}
                        >
                            <FormattedMessage
                                id="product.serial_number.listing.serial_header.label"
                                defaultMessage="Serial"
                            />
                        </SortLink>,
                        <FormattedMessage
                            key={0}
                            id="product.serial_number.listing.assigned_to_header.label"
                            defaultMessage="Assigned to"
                        />,
                        "",
                    ],
                    classNames: ["", "", ""]
                },
                rows: serialNumberData?.serialNumbers.map(sn => {
                    return getRow(sn)
                }) || [],
                pageSize: paginatedSearch.pageSize,
                totalItemsCount: totalItemsCount,
                showSelectionSize: false,
                smallLine: false,
                footer: { data: [], classNames: [] },
                footerClassName: ""
            }}

            search={<Search
                onChange={(savePrefs, refreshTotal, refreshStatBox, data, filtersToUse, searchKeywords) => {
                    handleKeywordsChange(searchKeywords)
                }}
                key={OBJECT_TYPE}
                typeOfSearch={OBJECT_TYPE}
                searchKeywords={paginatedSearch.keywords}
                searchBarClass={"col-md-6"}
                childrenClass={"col-md-2"}
                object={[OBJECT_TYPE]}
                placeholder={props.intl.formatMessage(messages["search_placeholder"])}
                hideFilters={true}
                hideFilterDropdown={true}
            >
                <div className="flex flex-column">
                    <div className="mb-4 d-flex justify-content-end">
                        <button
                            className="btn btn-primary"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault()
                                setModal(
                                    <EditSerialNumberModal
                                        productId={productId}
                                        onCloseCallback={() => {
                                            setModal(null)
                                        }}
                                        onSuccessCallback={() => {
                                            setModal(null)
                                            refetchListing()
                                        }}
                                    />
                                )
                            }}>
                            <Icon value={"plus-circle"} />
                            <FormattedMessage
                                id="product.serial_number.listing.add.label"
                                defaultMessage="Add"
                            />
                        </button>
                    </div>
                </div>
            </Search>}

            footer={
                <div className="col-12 d-flex d-flex justify-content-between align-items-center">
                    <div>
                        {pagination()}
                    </div>
                </div>
            }
        />
    </>
}

export default injectIntl(SerialNumberListing)



// WEBPACK FOOTER //
// ./src/product/serial-number/SerialNumberListing.tsx